import { ReactNode } from 'react'
import Check from '@mui/icons-material/Check'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Close from '@mui/icons-material/Close'
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { ApprovalAction, ApprovalStatePolicy } from '@/types/approvals'
import { AuthUser } from '@/types/users'
import {
  approverRowUserId,
  ApproverInfo,
  EntrySlotInfo,
  statePolicyApproverEntryRows,
} from '@/utils/approver-entries'
import { blockedSlotCopy } from '@/utils/blocked-slots'
import {
  MAX_VISIBLE_REVIEWERS,
  REVIEWER_AVATAR_OVERLAP,
  REVIEWER_AVATAR_SIZE,
  REVIEWER_CHEVRON_WIDTH,
} from '@/utils/my-approvals'
import { holdersLabel } from '@/utils/position-holders'
import { resolvePositionSignature } from '@/utils/position-signatures'
import { prettifyDate } from '@/utils/time'
import PositionAvatar from '@/components/avatars/PositionAvatar'
import UserAvatar from '@/components/avatars/UserAvatar'

type ApprovalReviewersProps = {
  policy: ApprovalStatePolicy
  /** Keyed by pd user id. */
  usersById: Map<string, AuthUser>
  usersPending: boolean
  /** Set on a history row: the action that row itself reports. */
  action?: ApprovalAction
}

type DecisionBadgeProps = { approved: boolean }

function DecisionBadge({ approved }: DecisionBadgeProps) {
  const theme = useTheme()
  const Icon = approved ? Check : Close
  return (
    <Box
      sx={{
        position: 'absolute',
        right: -2,
        bottom: -2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        height: 16,
        borderRadius: '50%',
        bgcolor: approved
          ? theme.palette.success.main
          : theme.palette.error.main,
        // The border is the gap between the badge and the avatar behind it.
        border: `2px solid ${theme.palette.background.paper}`,
      }}
    >
      <Icon sx={{ fontSize: 11, color: theme.palette.common.white }} />
    </Box>
  )
}

function slotLine(slot: EntrySlotInfo): string {
  const detail = slot.blocked
    ? blockedSlotCopy(slot.blocked).title
    : holdersLabel(slot.holders) ?? 'Whoever holds this position'
  return slot.startingPoints.length
    ? `${slot.startingPoints.join(', ')}: ${detail}`
    : detail
}

function signatureLine(
  action: ApprovalAction,
  positionName: string,
  coveredValues: string[],
): string {
  const decision = action.type === 'approval' ? 'Approved' : 'Rejected'
  const covered = coveredValues.length ? ` — ${coveredValues.join(', ')}` : ''
  return `${decision} as ${positionName} ${prettifyDate(
    action.created_at,
  )}${covered}`
}

export default function ApprovalReviewers({
  policy,
  usersById,
  usersPending,
  action,
}: ApprovalReviewersProps) {
  const rows = statePolicyApproverEntryRows(policy)
  const isSequential = policy.approval_order === 'sequential'
  const visibleRows = rows.slice(0, MAX_VISIBLE_REVIEWERS)
  const hiddenRows = rows.slice(MAX_VISIBLE_REVIEWERS)

  const userName = (userId: string | undefined) => {
    const user = userId ? usersById.get(userId) : undefined
    return user?.name || user?.email
  }

  // Null unless this row is the position the row's own action was taken
  // under. Both the visible tile and the `+N` list read it, so an overflowed
  // signature still says which position signed and what it covered.
  const positionSignature = (row: ApproverInfo): string | null => {
    if (!action || row.ref.kind !== 'position') return null
    if (action.position_id !== row.ref.positionId) return null
    const resolved = resolvePositionSignature(action, policy)
    return signatureLine(
      action,
      resolved?.positionName || row.positionName || 'Unknown position',
      resolved?.coveredValues ?? [],
    )
  }

  const hiddenRowLabel = (row: ApproverInfo): string =>
    row.ref.kind === 'position'
      ? positionSignature(row) ?? row.positionName ?? 'Unknown'
      : userName(approverRowUserId(row)) || 'Unknown'

  const renderRow = (row: ApproverInfo) => {
    if (row.ref.kind === 'position') {
      const positionName = row.positionName || 'Unknown position'
      const signature = positionSignature(row)

      const slots = row.slots ?? []
      const lines = [
        positionName,
        ...(slots.length
          ? slots.map(slotLine)
          : ["This position hasn't resolved to any slots."]),
      ]
      if (signature) lines.push(signature)

      const blocked = slots.some((slot) => !!slot.blocked)

      return {
        tooltipTitle: (
          <Box sx={{ whiteSpace: 'pre-line' }}>{lines.join('\n')}</Box>
        ),
        badgeApproved:
          signature && action ? action.type === 'approval' : undefined,
        avatarNode: <PositionAvatar blocked={blocked} />,
      }
    }

    const userId = approverRowUserId(row)
    const user = userId ? usersById.get(userId) : undefined
    const name = user?.name || user?.email
    const rowAction = row.latestAction
    const decidedLabel = rowAction
      ? `${
          rowAction.type === 'approval' ? 'Approved' : 'Rejected'
        } ${prettifyDate(rowAction.created_at)}${
          rowAction.acted_by_user_id
            ? ` by ${
                userName(rowAction.acted_by_user_id) || 'another user'
              } on their behalf. Reason: ${rowAction.reason || 'Not specified'}`
            : rowAction.reason
            ? `. Reason: ${rowAction.reason}`
            : ''
        }`
      : row.isCurrentTurn
      ? 'Their turn'
      : 'Not yet decided'

    return {
      tooltipTitle: `${name || 'Unknown'} — ${decidedLabel}` as ReactNode,
      badgeApproved: rowAction ? rowAction.type === 'approval' : undefined,
      avatarNode: <UserAvatar name={name} loading={usersPending && !user} />,
    }
  }

  return (
    <Stack direction="row" alignItems="center">
      {visibleRows.map((row, index) => {
        const { tooltipTitle, badgeApproved, avatarNode } = renderRow(row)

        return (
          <Stack key={row.key} direction="row" alignItems="center">
            {isSequential && index > 0 && (
              <ChevronRight
                sx={{
                  fontSize: REVIEWER_CHEVRON_WIDTH,
                  color: 'text.disabled',
                }}
              />
            )}
            <Tooltip title={tooltipTitle}>
              <Box
                sx={{
                  position: 'relative',
                  width: REVIEWER_AVATAR_SIZE,
                  height: REVIEWER_AVATAR_SIZE,
                  flex: 'none',
                  ml:
                    !isSequential && index > 0
                      ? `-${REVIEWER_AVATAR_OVERLAP}px`
                      : 0,
                  // Left-most on top, so the cluster reads left to right.
                  zIndex: rows.length - index,
                  // Direct child only -- a descendant selector would also
                  // resize the decision badge.
                  '& > .MuiAvatar-root, & > .MuiSkeleton-root': {
                    width: REVIEWER_AVATAR_SIZE,
                    height: REVIEWER_AVATAR_SIZE,
                    fontSize: 11,
                  },
                }}
              >
                {avatarNode}
                {badgeApproved !== undefined && (
                  <DecisionBadge approved={badgeApproved} />
                )}
              </Box>
            </Tooltip>
          </Stack>
        )
      })}
      {hiddenRows.length > 0 && (
        <Stack direction="row" alignItems="center">
          {isSequential && (
            <ChevronRight
              sx={{ fontSize: REVIEWER_CHEVRON_WIDTH, color: 'text.disabled' }}
            />
          )}
          <Tooltip
            title={
              <Box sx={{ whiteSpace: 'pre-line' }}>
                {hiddenRows.map(hiddenRowLabel).join('\n')}
              </Box>
            }
          >
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ flex: 'none', ml: isSequential ? 0.25 : 0.75 }}
            >
              +{hiddenRows.length}
            </Typography>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  )
}
